import React, { useState, useEffect } from "react";
import {
  TextInput,
  Select,
  Textarea,
  Checkbox,
  Button,
  Stack,
  Group,
  Text,
  Box,
  Grid,
  Alert,
  NumberInput,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import StandardModal from "../shared/Modal/StandardModal";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { parseFormattedNumber } from "../../utils/formatters";
import "./EditProjectedRevenueModal.css";

function EditRepInfoModal({
  isOpen,
  onClose,
  rep,
  reps = [],
  repTypes = [],
  recruitingSourceTypes = [],
  onUpdate,
}) {
  const { getVerifiedJWT } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    instagram: "",
    linkedin: "",
    recruitedBy: "",
    manager: "",
    isManager: false,
    repRank: "",
    recruitingSource: "",
    recruitingSourceNotes: "",
    includeDownline: false,
    estimatedSeasonStartDate: "",
    estimatedSeasonEndDate: "",
    projectedRevenue: "",
  });

  // Initialize form data when modal opens or rep changes
  useEffect(() => {
    if (rep && isOpen) {
      setFormData({
        firstName: rep.first_name || "",
        lastName: rep.last_name || "",
        email: rep.email || "",
        phoneNumber: rep.phone_number || "",
        instagram: rep.instagram || "",
        linkedin: rep.linked_in || "",
        recruitedBy:
          rep.recruiter && rep.recruiter.user && rep.recruiter.user.rep_id
            ? String(rep.recruiter.user.rep_id)
            : "",
        manager:
          rep.manager && rep.manager.user && rep.manager.user.rep_id
            ? String(rep.manager.user.rep_id)
            : "",
        isManager: rep.is_manager || false,
        repRank: rep.rep_type_id ? String(rep.rep_type_id) : "",
        recruitingSource: rep.recruiting_source_id
          ? String(rep.recruiting_source_id)
          : "",
        recruitingSourceNotes: rep.recruiting_source_notes || "",
        includeDownline: rep.include_downline || false,
        estimatedSeasonStartDate: rep.sale_start_date
          ? rep.sale_start_date.split("T")[0]
          : "",
        estimatedSeasonEndDate: rep.sale_end_date
          ? rep.sale_end_date.split("T")[0]
          : "",
        projectedRevenue: rep.projected_revenue || "",
      });
    }
  }, [rep, isOpen, reps]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      // Prepare API data
      const apiData = {
        first_name: formData.firstName || null,
        last_name: formData.lastName || null,
        email: formData.email || null,
        phone_number: formData.phoneNumber || null,
        instagram_username: formData.instagram || null,
        linkedin_profile: formData.linkedin || null,
        recruited_by: formData.recruitedBy
          ? parseInt(formData.recruitedBy)
          : null,
        manager: formData.manager ? parseInt(formData.manager) : null,
        is_manager: formData.isManager,
        rep_type_id: formData.repRank ? parseInt(formData.repRank) : null,
        recruiting_source_id: formData.recruitingSource
          ? parseInt(formData.recruitingSource)
          : null,
        recruiting_source_notes: formData.recruitingSourceNotes || null,
        include_downline: formData.includeDownline,
        sale_start_date: formData.estimatedSeasonStartDate || null,
        sale_end_date: formData.estimatedSeasonEndDate || null,
        projected_revenue: formData.projectedRevenue
          ? parseFloat(parseFormattedNumber(formData.projectedRevenue))
          : null,
      };

      const response = await fetch(
        getApiEndpoint(`/v1/rep/details/${rep.rep_id}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(apiData),
        }
      );

      if (response.ok) {
        // Trigger parent component to refetch fresh data from API
        onUpdate();
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || "Failed to update rep information");
      }
    } catch (err) {
      setError("Failed to update rep information");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (rep) {
      setFormData({
        firstName: rep.first_name || "",
        lastName: rep.last_name || "",
        email: rep.email || "",
        phoneNumber: rep.phone_number || "",
        instagram: rep.instagram || "",
        linkedin: rep.linked_in || "",
        recruitedBy: rep.recruited_by ? String(rep.recruited_by) : "",
        manager: rep.manager ? String(rep.manager) : "",
        isManager: rep.is_manager || false,
        repRank: rep.rep_type_id ? String(rep.rep_type_id) : "",
        recruitingSource: rep.recruiting_source_id
          ? String(rep.recruiting_source_id)
          : "",
        recruitingSourceNotes: rep.recruiting_source_notes || "",
        includeDownline: rep.include_downline || false,
        estimatedSeasonStartDate: rep.sale_start_date
          ? rep.sale_start_date.split("T")[0]
          : "",
        estimatedSeasonEndDate: rep.sale_end_date
          ? rep.sale_end_date.split("T")[0]
          : "",
        projectedRevenue: rep.projected_revenue || "",
      });
    }
    setError(null);
    onClose();
  };

  // Get recruiters and managers for dropdowns
  const recruiters = reps.map(rep => ({
    id: rep.rep_id,
    name: `${rep.first_name} ${rep.last_name}`,
  }));

  const managers = reps
    .filter(rep => rep.is_manager)
    .map(rep => ({
      id: rep.rep_id,
      name: `${rep.first_name} ${rep.last_name}`,
    }));

  // Prepare select data for Mantine Select components
  const recruiterData = [
    { value: "", label: "Select recruiter..." },
    ...recruiters.map(recruiter => ({
      value: String(recruiter.id),
      label: recruiter.name,
    })),
  ];

  const managerData = [
    { value: "", label: "Select manager..." },
    ...managers.map(manager => ({
      value: String(manager.id),
      label: manager.name,
    })),
  ];

  const repTypeData = [
    { value: "", label: "Select rep rank..." },
    ...repTypes.map(repType => ({
      value: String(repType.id),
      label: repType.name,
    })),
  ];

  const recruitingSourceData = [
    { value: "", label: "Select recruiting source..." },
    ...recruitingSourceTypes.map(sourceType => ({
      value: String(sourceType.id),
      label: sourceType.source_name,
    })),
  ];

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Edit Rep Information"
      footerLeft={<div></div>}
      footerRight={
        <Group justify="flex-end">
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading}>
            Save
          </Button>
        </Group>
      }
    >
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          mb="md"
        >
          {error}
        </Alert>
      )}
      <Stack gap="lg">
        {/* Personal Information Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Personal Information
          </Text>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="First Name"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={e =>
                  setFormData(prev => ({ ...prev, firstName: e.target.value }))
                }
                disabled={loading}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Last Name"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={e =>
                  setFormData(prev => ({ ...prev, lastName: e.target.value }))
                }
                disabled={loading}
                required
              />
            </Grid.Col>
          </Grid>
          <Grid mt="md">
            <Grid.Col span={6}>
              <TextInput
                label="Email"
                placeholder="Enter email address"
                type="email"
                value={formData.email}
                onChange={e =>
                  setFormData(prev => ({ ...prev, email: e.target.value }))
                }
                disabled={loading}
                required
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Phone Number"
                placeholder="Enter phone number"
                type="tel"
                value={formData.phoneNumber}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    phoneNumber: e.target.value,
                  }))
                }
                disabled={loading}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Social Media Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Social Media
          </Text>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Instagram"
                placeholder="@username or profile URL"
                value={formData.instagram}
                onChange={e =>
                  setFormData(prev => ({ ...prev, instagram: e.target.value }))
                }
                disabled={loading}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="LinkedIn"
                placeholder="LinkedIn profile URL"
                value={formData.linkedin}
                onChange={e =>
                  setFormData(prev => ({ ...prev, linkedin: e.target.value }))
                }
                disabled={loading}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Management Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Management
          </Text>
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Recruited By"
                placeholder="Select recruiter..."
                value={formData.recruitedBy}
                onChange={value =>
                  setFormData(prev => ({ ...prev, recruitedBy: value || "" }))
                }
                data={recruiterData}
                disabled={loading}
                searchable
                clearable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Manager"
                placeholder="Select manager..."
                value={formData.manager}
                onChange={value =>
                  setFormData(prev => ({ ...prev, manager: value || "" }))
                }
                data={managerData}
                disabled={loading}
                searchable
                clearable
              />
            </Grid.Col>
          </Grid>
          <Checkbox
            label="Is Manager?"
            checked={formData.isManager}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                isManager: e.currentTarget.checked,
              }))
            }
            disabled={loading}
            mt="md"
          />
        </Box>

        {/* Rep Details Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Rep Details
          </Text>
          <Select
            label="Rep Rank"
            placeholder="Select rep rank..."
            value={formData.repRank}
            onChange={value =>
              setFormData(prev => ({ ...prev, repRank: value || "" }))
            }
            data={repTypeData}
            disabled={loading}
            searchable
            clearable
            mb="md"
          />
          <Select
            label="Recruiting Source"
            placeholder="Select recruiting source..."
            value={formData.recruitingSource}
            onChange={value =>
              setFormData(prev => ({ ...prev, recruitingSource: value || "" }))
            }
            data={recruitingSourceData}
            disabled={loading}
            searchable
            clearable
            mb="md"
          />
          <Textarea
            label="Recruiting Source Notes"
            placeholder="Enter recruiting source notes..."
            value={formData.recruitingSourceNotes}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                recruitingSourceNotes: e.target.value,
              }))
            }
            disabled={loading}
            rows={3}
            mb="md"
          />
          <Checkbox
            label="Include Downline in Groups"
            description="Automatically add this rep's downline when they join any group"
            checked={formData.includeDownline}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                includeDownline: e.currentTarget.checked,
              }))
            }
            disabled={loading}
          />
        </Box>

        {/* Season Dates Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Season Dates
          </Text>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                type="date"
                label="Estimated Season Start Date"
                value={formData.estimatedSeasonStartDate}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    estimatedSeasonStartDate: e.target.value,
                  }))
                }
                disabled={loading}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                type="date"
                label="Estimated Season End Date"
                value={formData.estimatedSeasonEndDate}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    estimatedSeasonEndDate: e.target.value,
                  }))
                }
                disabled={loading}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Revenue Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Revenue
          </Text>
          <NumberInput
            label="Projected Revenue"
            placeholder="0"
            value={
              formData.projectedRevenue
                ? parseFloat(parseFormattedNumber(formData.projectedRevenue))
                : 0
            }
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                projectedRevenue: value ? String(value) : "",
              }))
            }
            disabled={loading}
            thousandSeparator=","
            prefix="$"
            decimalScale={2}
            fixedDecimalScale
          />
          <Text size="xs" c="dimmed" mt="xs">
            Enter the estimated revenue for this representative
          </Text>
        </Box>
      </Stack>
    </StandardModal>
  );
}

export default EditRepInfoModal;
